import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { api } from '../../../lib/api';

type StudentLite = { id: number; full_name: string; email?: string | null };

type ExportFormat = 'pdf' | 'csv' | 'html' | 'word';

interface Props {
  open: boolean;
  onClose: () => void;
  students: StudentLite[];
  configId: number;
  schoolYearLabel?: string | null;
}

function filenameFromContentDisposition(cd: string | undefined | null): string | null {
  if (!cd) return null;
  const m = cd.match(/filename=\"?([^\";]+)\"?/i);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

function extForFormat(fmt: ExportFormat): string {
  if (fmt === 'csv') return 'csv';
  if (fmt === 'html') return 'html';
  if (fmt === 'word') return 'doc';
  return 'pdf';
}

const MatricolaExportModal: React.FC<Props> = ({ open, onClose, students, configId, schoolYearLabel }) => {
  const [studentId, setStudentId] = useState<number | ''>('');
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const student = useMemo(() => students.find((s) => s.id === studentId) || null, [students, studentId]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (!studentId && students.length) setStudentId(students[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const download = async () => {
    if (!studentId) {
      setError('Selecteaza un elev.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/catalog/foaie-matricola', {
        params: { config_id: configId, student_id: studentId, format },
        responseType: 'blob',
      });

      const cd = (res.headers?.['content-disposition'] as string | undefined) || null;
      const serverName = filenameFromContentDisposition(cd);
      const fallbackName = `foaie_matricola_${(schoolYearLabel || 'catalog').replace(/\s+/g, '_')}_${(student?.full_name || 'elev').replace(/\s+/g, '_')}.${extForFormat(format)}`;
      const filename = serverName || fallbackName;

      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Nu s-a putut descarca foaia matricola.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="w-full max-w-lg rounded-xl border border-border bg-background shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="FileDown" size={18} />
            </div>
            <div>
              <div className="font-semibold text-foreground">Foaie matricola</div>
              <div className="text-xs text-muted-foreground">
                {schoolYearLabel ? `Catalog: ${schoolYearLabel}` : 'Export din catalogul selectat'}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border hover:bg-muted/40"
            onClick={onClose}
            title="Inchide"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {error && <div className="rounded-md border border-error/20 bg-error/10 text-error p-2 text-sm">{error}</div>}

          <div>
            <div className="text-sm font-medium text-foreground mb-1">Elev</div>
            <select
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value ? Number(e.target.value) : '')}
            >
              {students.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.full_name}
                </option>
              ))}
            </select>
            {student?.email ? <div className="text-xs text-muted-foreground mt-1">{student.email}</div> : null}
          </div>

          <div>
            <div className="text-sm font-medium text-foreground mb-1">Format</div>
            <div className="grid grid-cols-2 gap-2">
              {(['pdf', 'word', 'html', 'csv'] as ExportFormat[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFormat(f)}
                  className={`h-10 rounded-md border px-3 text-sm flex items-center justify-between ${
                    format === f ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                  }`}
                >
                  <span className="capitalize">{f === 'word' ? 'Word' : f.toUpperCase()}</span>
                  {format === f ? <Icon name="Check" size={16} /> : null}
                </button>
              ))}
            </div>
            {format === 'html' ? (
              <div className="text-xs text-muted-foreground mt-2">Tip: HTML poate fi tiparit din browser ca PDF.</div>
            ) : null}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Renunta
          </Button>
          <Button onClick={download} loading={loading} iconName="Download" iconPosition="left">
            Descarca
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MatricolaExportModal;

